import { useState } from 'react'

// BL
import { TransactionRowModel } from '../shared'

// UI
import { Table, Button, Icon } from 'semantic-ui-react'
import { DATE_FORMATS_LUXON, toUTCDateString } from '@/utils/dateHelpers'
const MAX_DESCRIPTION_LENGTH = 40
const LOCKED_HINT =
  'This reconciliation is complete. Reopen it to change or remove its transactions.'

interface Props {
  transaction: TransactionRowModel
  onCheckButtonClicked: (t: TransactionRowModel) => Promise<void>
  onDeleteClicked: (t: TransactionRowModel) => Promise<void>
  locked: boolean
}

const PanelTableRow = ({
  transaction,
  onCheckButtonClicked,
  onDeleteClicked,
  locked,
}: Props) => {
  const [loading, setLoading] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const className = transaction.matched ? 'matched' : 'unmatched'

  function handleError(err: unknown) {
    alert(err)
    setLoading(false)
    setDeleting(false)
  }

  const onCheckButtonClickedChild = async () => {
    setLoading(true)

    try {
      await onCheckButtonClicked(transaction)
      setLoading(false)
    } catch (err) {
      handleError(err)
    }
  }

  const onDeletePrompt = async () => {
    const result = confirm('Are you sure you want to delete this transaction?')
    if (result) {
      try {
        setDeleting(true)
        await onDeleteClicked(transaction)
        setDeleting(false)
      } catch (err) {
        handleError(err)
      }
    }

    return
  }

  const fullDescription = transaction.description ?? ''
  const description =
    fullDescription.length > MAX_DESCRIPTION_LENGTH
      ? `${fullDescription.substring(0, MAX_DESCRIPTION_LENGTH)}...`
      : fullDescription

  return (
    <Table.Row className={className} key={transaction.id}>
      <Table.Cell>
        <Button
          onClick={onDeletePrompt}
          icon="delete"
          className="delete-transaction-button mute"
          title={locked ? LOCKED_HINT : undefined}
          disabled={deleting || locked}
          loading={deleting}
        />
      </Table.Cell>
      <Table.Cell>
        {toUTCDateString({
          value: transaction.date,
          format: DATE_FORMATS_LUXON.DISPLAY_SHORT,
        })}
      </Table.Cell>
      <Table.Cell>
        {transaction.manualUpload ? <Icon name="upload" size="large" /> : ''}
      </Table.Cell>
      <Table.Cell>{description}</Table.Cell>
      <Table.Cell>{transaction.debit}</Table.Cell>
      <Table.Cell>{transaction.credit}</Table.Cell>
      <Table.Cell>
        {transaction.matched && (
          <Button
            disabled={loading || locked}
            title={locked ? LOCKED_HINT : undefined}
            loading={loading}
            onClick={onCheckButtonClickedChild}
            className="mute matched"
            icon={`${loading ? 'circle outline' : 'check'}`}
          />
        )}
        {!transaction.matched && (
          <Button
            disabled={loading || locked}
            title={locked ? LOCKED_HINT : undefined}
            loading={loading}
            onClick={onCheckButtonClickedChild}
            style={{ background: 'transparent' }}
            className="unmatched"
            icon="circle outline"
          />
        )}
      </Table.Cell>
    </Table.Row>
  )
}

export default PanelTableRow
